import { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { concatMap, filter, take, timer } from 'rxjs';
import { RxUtils, Tr } from '@utils';
import { useLoggedinUser, useService } from '@hooks';
import { siteService, carListBandService, simulationService, Permission, SimulationStatus, Simulation } from '@services';
import { Button, Stack, Typography } from '@mui/material';
import { GeneratingResultsDialog, PortalPageTitle } from '@components/portal/common';
import { ExecutiveSummaryLayout, RemainingTasksLayout } from '@components/portal/dashboard';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';

export const StandardDashboard: FC = () => {
	const user = useLoggedinUser();
	const loc = useLocation();
	// const navigate = useNavigate();
	const [simulationInProgress, setSimulationInProgress] = useState(false);
	const [simulation, setSimulation] = useState<Simulation>();
	const { data: locations } = useService(() => siteService.getAllFromCache());
	const { data: carListBands } = useService(() => carListBandService.getAllFromCache());
	const remainingTaskCount = 2 - [locations?.length, carListBands?.length].filter(Boolean).length;
	const hasRemainingTasks = remainingTaskCount > 0;
	const getSubtitle = () => {
		if (remainingTaskCount > 1) {
			return <Tr.Portal path="dashboard.remaining-tasks" options={{ task: remainingTaskCount }} />;
		} else if (remainingTaskCount === 1) {
			return <Tr.Portal path="dashboard.remaining-task" options={{ task: remainingTaskCount }} />;
		} else {
			if (simulation?.outDated) {
				return (
					<Stack direction="row" spacing={1} sx={{ color: 'warning.700' }}>
						<NotificationImportantIcon color="inherit" fontSize="small" />
						<Typography sx={{ color: 'inherit' }}>
							<Tr.Portal path="dashboard.update-simulation-subtitle" />
						</Typography>
					</Stack>
				);
			} else {
				return <Tr.Portal path="dashboard.subtitle-executive-summary" />;
			}
		}
	};
	const onCreateTask = () => RxUtils.promisify(simulationService.create(), () => setSimulationInProgress(true));

	useEffect(() => {
		const subscription = timer(0, 5000)
			.pipe(
				concatMap(() => simulationService.getLatest(true)),
				filter((result) => !!result),
				take(12)
			)
			.subscribe({
				next: (result) => {
					const isSameSimulation = simulation && simulation.id === result.id;
					if ([SimulationStatus.IN_PROGRESS, SimulationStatus.CREATED].includes(result.status)) {
						setSimulationInProgress(true);
						setSimulation(undefined);
					} else {
						setSimulationInProgress(false);
						if ((loc.state as any)?.auto) {
							RxUtils.promisify(simulationService.getTasks(result)); // trigger tasks fetching
							// navigate(`/portal/${PortalRoutePath.WIZARD}/start`, { state: { auto: true } });
						}
						if (simulation) {
							!isSameSimulation && setSimulation(result);
						} else {
							setSimulation(result);
						}
					}
				},
				error: () => {
					setSimulationInProgress(false);
					setSimulation(undefined);
				},
				complete: () => {
					setSimulationInProgress(false);
					setSimulation(undefined);
				}
			});

		return () => subscription.unsubscribe();
	}, [loc, simulation]);

	useEffect(() => {
		(loc.state as any)?.auto && onCreateTask();
	}, []);

	return (
		<Stack
			direction="column"
			spacing={4}
			sx={{ position: 'relative', height: 1.0, maxHeight: 1.0, paddingX: 9, paddingY: 5, overflowY: 'auto' }}
		>
			<PortalPageTitle
				title={<Tr.Portal path={hasRemainingTasks ? 'dashboard.title-onboarding' : 'dashboard.title-executive-summary'} />}
				subtitle={getSubtitle()}
			>
				{user?.hasPermission(Permission.RUN_SIMULATION) && !hasRemainingTasks ? (
					<Button
						data-testid="run-simulation-button"
						variant="contained"
						color={simulation?.outDated ? 'primary' : 'secondary'}
						size="small"
						onClick={onCreateTask}
						disabled={simulationInProgress}
					>
						<Tr.Portal path={simulation?.outDated ? 'dashboard.update-simulation' : 'dashboard.start-simulation'} />
					</Button>
				) : null}
			</PortalPageTitle>
			{hasRemainingTasks ? (
				<RemainingTasksLayout carListBandTaskCompleted={!!carListBands?.length} locationTaskCompleted={!!locations?.length} />
			) : (
				<ExecutiveSummaryLayout simulation={simulation} />
			)}
			<GeneratingResultsDialog
				hideCloseIcon
				open={simulationInProgress}
				contentTitle={<Tr.Portal path="generating-result-dialog.content-simulation-run" />}
			/>
		</Stack>
	);
};
